import { defineStore } from 'pinia';
import TimesheetAPI from '@api/timesheet';
import type { TimesheetEntryBreak } from '@/types/TimesheetEntryBreak';

export interface TimesheetEntry {
    id: number;
    time_in?: string;
    time_out?: string | null;
    away?: boolean;
    breaks?: TimesheetEntryBreak[];
    [key: string]: unknown;
}

interface AnalyticsData {
    analytics?: {
        pending_timesheets?: number;
        overdue_timesheets?: number;
        total_staff?: number;
        this_week_hours?: number;
    };
    [key: string]: unknown;
}

interface TimesheetData {
    is_locked?: number;
    [key: string]: unknown;
}

export const useTimesheetStore = defineStore('timesheet', () => {
    const timesheets = ref<Record<string, unknown>>({});
    const timesheetData = ref<TimesheetData>({});
    const currentTimesheetEntry = ref<TimesheetEntry | null>(null);
    const timesheetEntries = ref<TimesheetEntry[]>([]);
    const upcomingJobs = ref<Record<string, unknown>[]>([]);
    const analyticsData = ref<AnalyticsData>({});
    const jobSearchResults = ref<Record<string, unknown>[]>([]);
    const taskSearchResults = ref<Record<string, unknown>[]>([]);

    // Computed
    const getTimesheets = computed(() => timesheets.value);
    const getCurrentTimesheetEntry = computed(() => currentTimesheetEntry.value);
    const getTimesheetData = computed(() => timesheetData.value);
    const getTimesheetEntries = computed(() => timesheetEntries.value);
    const getUpcomingJobs = computed(() => upcomingJobs.value);

    const isClockedIn = computed(() => {
        const entry = currentTimesheetEntry.value;
        return !!(entry?.time_in && !entry.time_out);
    });

    const isLocked = computed(() => timesheetData.value?.is_locked === 1);

    const pendingTimesheets = computed(() => analyticsData.value?.analytics?.pending_timesheets || 0);
    const overdueTimesheets = computed(() => analyticsData.value?.analytics?.overdue_timesheets || 0);
    const totalStaff = computed(() => analyticsData.value?.analytics?.total_staff || 0);
    const thisWeekHours = computed(() => analyticsData.value?.analytics?.this_week_hours || 0);

    const isAllTimesheetEntriesMarkedAway = computed(() => {
        if (!timesheetEntries.value.length) return false;
        return timesheetEntries.value.every((entry) => Boolean(entry.away));
    });

    // Functions
    async function fetchTimesheet(params: Record<string, unknown>) {
        try {
            const response = await TimesheetAPI.getTimesheet(params);
            timesheets.value = response.data.data || {};
            return response;
        } catch (error) {
            console.error('TIMESHEET ERROR: ', error);
            throw error;
        }
    }

    async function fetchTimesheetByUserAndWeek(params: Record<string, unknown>) {
        try {
            const response = await TimesheetAPI.getTimesheetByUserAndWeek(params);
            timesheetData.value = response.data.timesheet || {};
            return response;
        } catch (error) {
            console.error('TIMESHEET BY USER AND WEEK ERROR: ', error);
            throw error;
        }
    }

    async function fetchUpcomingJobs(params: Record<string, unknown>, user: number | string) {
        try {
            const response = await TimesheetAPI.upcomingJobs(params, user);
            upcomingJobs.value = response.data.data || [];
            return response;
        } catch (error) {
            console.error('UPCOMING JOBS ERROR: ', error);
            throw error;
        }
    }

    async function createTimesheet(data: Record<string, unknown>) {
        try {
            const response = await TimesheetAPI.createTimesheet(data);
            return response;
        } catch (error) {
            console.error('CREATE TIMESHEET ERROR: ', error);
            throw error;
        }
    }

    async function clockIn(data: Record<string, unknown>) {
        try {
            const response = await TimesheetAPI.clockIn(data);
            return response;
        } catch (error) {
            console.error('Clock in Error: ', error);
            throw error;
        }
    }

    async function clockOut(data: Record<string, unknown>, timesheetEntry: number | string) {
        try {
            const response = await TimesheetAPI.clockOut(data, timesheetEntry);
            return response;
        } catch (error) {
            console.error('Clock out Error: ', error);
            throw error;
        }
    }

    async function bulkUpdateTimesheetEntries(data: Record<string, unknown>) {
        try {
            const response = await TimesheetAPI.bulkUpdateTimesheetEntries(data);
            return response;
        } catch (error) {
            console.error('BULK UPDATE ERROR: ', error);
            throw error;
        }
    }

    async function fetchCurrentTimesheetEntry(params: Record<string, unknown> = {}) {
        try {
            const response = await TimesheetAPI.getCurrentTimesheetEntry(params);
            currentTimesheetEntry.value = response.data || null;
        } catch (error) {
            console.error('TIMESHEET ERROR: ', error);
            currentTimesheetEntry.value = null;
            throw error;
        }
    }

    async function fetchTimesheetById(timesheet: number | string) {
        try {
            const response = await TimesheetAPI.getTimesheetById(timesheet);
            timesheetData.value = response.data.data || {};
            return response.data.data || {};
        } catch (error) {
            console.error('TIMESHEET ERROR: ', error);
            throw error;
        }
    }

    async function timesheetApprove(timesheet: number | string, data: Record<string, unknown>) {
        return await TimesheetAPI.timesheetApprove(timesheet, data);
    }

    async function timesheetDecline(timesheet: number | string, data: Record<string, unknown>) {
        return await TimesheetAPI.timesheetDecline(timesheet, data);
    }

    async function fetchTimesheetEntriesByTimesheet(timesheet: number | string) {
        try {
            const response = await TimesheetAPI.getTimesheetEntriesByTimesheet(timesheet);
            timesheetEntries.value = response.data.data || [];
            return response.data.data || [];
        } catch (error) {
            console.error('TIMESHEET ENTRIES ERROR: ', error);
            throw error;
        }
    }

    async function createTimesheetEntry(timesheet: number | string, data: Record<string, unknown>) {
        return await TimesheetAPI.createTimesheetEntry(timesheet, data);
    }

    async function updateTimesheetEntry(
        timesheet: number | string,
        data: Record<string, unknown>,
        timesheetEntry: number | string,
    ) {
        return await TimesheetAPI.updateTimesheetEntry(timesheet, data, timesheetEntry);
    }

    function reset() {
        timesheets.value = {};
        timesheetData.value = {};
        currentTimesheetEntry.value = null;
        timesheetEntries.value = [];
    }

    function clearTimesheetEntries() {
        timesheetEntries.value = [];
    }

    async function fetchAnalytics(params: Record<string, unknown> = {}) {
        const response = await TimesheetAPI.analytics(params);
        analyticsData.value = response.data || {};
        return response;
    }

    async function searchOrder(params: Record<string, unknown> = {}) {
        const response = await TimesheetAPI.searchOrder(params);
        jobSearchResults.value = response.data.data || [];
        return response;
    }

    async function searchTask(params: Record<string, unknown> = {}) {
        const response = await TimesheetAPI.searchTask(params);
        taskSearchResults.value = response.data.data || [];
        return response;
    }

    return {
        fetchTimesheet,
        fetchTimesheetByUserAndWeek,
        fetchUpcomingJobs,
        clockIn,
        clockOut,
        bulkUpdateTimesheetEntries,
        fetchCurrentTimesheetEntry,
        fetchTimesheetById,
        timesheetApprove,
        timesheetDecline,
        createTimesheet,

        getTimesheets,
        getCurrentTimesheetEntry,
        getTimesheetData,
        getTimesheetEntries,
        isLocked,
        getUpcomingJobs,
        isClockedIn,

        createTimesheetEntry,
        updateTimesheetEntry,
        fetchTimesheetEntriesByTimesheet,

        reset,
        clearTimesheetEntries,

        fetchAnalytics,
        searchOrder,
        searchTask,

        pendingTimesheets,
        overdueTimesheets,
        totalStaff,
        thisWeekHours,
        isAllTimesheetEntriesMarkedAway,
        jobSearchResults,
        taskSearchResults,
    };
});
